/**
 * Daily Workspace — the screen the product is for.
 *
 * Answers ONE question: what should I work on today. Organized by what you must DO, not by record
 * type. Waiting is its own group, separate from the action list, because the whole point is that
 * those relationships need nothing from you — that separation is what prevents the unnecessary
 * third email.
 *
 * ONE upstream call builds the whole page.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { tfApi } from '../api/tf_api'
import { useApp } from '../contexts/app_context'
import { PageHeader } from '../components/layout/app_shell'
import {
  DueBadge,
  EmptyState,
  ErrorState,
  ModalShell,
  Spinner,
  StatusBadge,
  addDaysIso,
  formatShortDate,
} from '../components/ui/primitives'
import type { BucketKey, Dashboard, FollowUp, RelationshipRow } from '../types/tf_types'

/** The order a person should work them, not an arbitrary sort. */
const GROUPS: Array<{ key: BucketKey; title: string; icon: string; tone: string; note?: string }> = [
  { key: 'OVERDUE', title: 'Overdue', icon: 'error', tone: 'overdue' },
  { key: 'TODAY', title: 'Due Today', icon: 'today', tone: 'today' },
  { key: 'TOMORROW', title: 'Tomorrow', icon: 'event', tone: '' },
  { key: 'THIS_WEEK', title: 'This Week', icon: 'date_range', tone: '' },
  {
    key: 'WAITING',
    title: 'Waiting on Them',
    icon: 'hourglass_top',
    tone: 'waiting',
    note: 'These need nothing from you — the other person owes the reply. Each carries a date to chase if it goes quiet.',
  },
  { key: 'NO_ACTION', title: 'No Next Move Recorded', icon: 'help', tone: '' },
]

export function DailyWorkspacePage() {
  const { notify, notifyError } = useApp()
  const [data, setData] = useState<Dashboard | null>(null)
  const [followUps, setFollowUps] = useState<FollowUp[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<{ message: string; technical?: string } | null>(null)
  const [completing, setCompleting] = useState<{ row: RelationshipRow; followUp: FollowUp } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [dashboard, pending] = await Promise.all([
        tfApi.fetchDashboard(),
        tfApi.fetchFollowUps({ follow_up_status: 'Pending' }),
      ])
      setData(dashboard)
      setFollowUps(pending.Items)
    } catch (err: any) {
      setError({ message: err?.detail?.message ?? String(err), technical: err?.detail?.technical })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const followUpFor = (contactId: number) =>
    followUps.find((f) => f.SubjectType === 'PERSON' && f.SubjectId === contactId) ?? null

  const defer = async (followUp: FollowUp, days: number) => {
    try {
      await tfApi.deferFollowUp(followUp.FollowUpId, addDaysIso(days))
      notify({ tone: 'ok', title: 'Deferred', body: `Moved to ${formatShortDate(addDaysIso(days))}` })
      await load()
    } catch (err) {
      notifyError('Could not defer', err)
    }
  }

  const stopWaiting = async (row: RelationshipRow) => {
    try {
      await tfApi.setWaiting(row.ContactId, { WaitingForResponse: false })
      notify({ tone: 'ok', title: 'They replied', body: `${row.DisplayName} is back in your action list` })
      await load()
    } catch (err) {
      notifyError('Could not update waiting state', err)
    }
  }

  if (loading) return <><PageHeader crumb="Tidalfin / Home" title="Daily Workspace" /><Spinner /></>

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Home"
        title="Daily Workspace"
        description={
          data
            ? `${new Date(`${data.AsOfDate}T00:00:00`).toLocaleDateString(undefined, {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
              })} — what needs your attention.`
            : undefined
        }
        actions={
          <>
            <button className="btn secondary" type="button" onClick={() => void load()}>
              <span className="material-symbols-outlined">refresh</span>Refresh
            </button>
            <Link className="btn primary" to="/home/my-relationships">
              <span className="material-symbols-outlined">diversity_3</span>My Relationships
            </Link>
          </>
        }
      />

      <div className="body-scroll">
        {error && <ErrorState title="Could not load the workspace" message={error.message} technical={error.technical} onRetry={() => void load()} />}

        {data && (
          <>
            <div className="stats">
              {data.Cards.map((card) => (
                <div className={`stat${card.Tone ? ` ${card.Tone}` : ''}`} key={card.Key}>
                  <span className="stat-label">{card.Label}</span>
                  <span className="stat-value">{card.Value}</span>
                  {card.Sub && <span className="stat-sub">{card.Sub}</span>}
                </div>
              ))}
            </div>

            {GROUPS.every((g) => (data.Buckets[g.key] ?? []).length === 0) && (
              <EmptyState
                icon="check_circle"
                title="Nothing needs your attention"
                message="No overdue or due follow-ups. Add a next move to a relationship to get started."
                action={<Link className="btn primary" to="/home/my-relationships">Open My Relationships</Link>}
              />
            )}

            {GROUPS.map((group) => {
              const rows = data.Buckets[group.key] ?? []
              if (!rows.length) return null
              return (
                <div className="queue-group" key={group.key}>
                  <div className={`queue-head ${group.tone}`}>
                    <span className="material-symbols-outlined">{group.icon}</span>
                    <span className="queue-name">{group.title}</span>
                    <span className="queue-count">{rows.length}</span>
                  </div>

                  {group.note && <p className="proto-note">{group.note}</p>}

                  {rows.map((row) => {
                    const followUp = followUpFor(row.ContactId)
                    const waiting = group.key === 'WAITING'
                    return (
                      <article
                        className={`wq-item${
                          group.key === 'OVERDUE' ? ' is-overdue'
                            : group.key === 'TODAY' ? ' is-today'
                            : waiting ? ' is-waiting'
                            : group.key === 'NO_ACTION' ? ' is-hold' : ''
                        }`}
                        key={row.ContactId}
                      >
                        <div className="wq-main">
                          <div className="wq-title">
                            <Link className="wq-who" to={`/relationships/people/${row.ContactId}`}>
                              {row.DisplayName}
                            </Link>
                            {row.OrganizationName && <span className="wq-org">· {row.OrganizationName}</span>}
                            <StatusBadge domain="RELATIONSHIP_STRENGTH" value={row.RelationshipStrength} />
                            {row.ContactStatus && <StatusBadge domain="CONTACT_STATUS" value={row.ContactStatus} />}
                          </div>

                          <div className="wq-move">
                            <span className="material-symbols-outlined">arrow_forward</span>
                            {row.NextMove || <span style={{ opacity: 0.7 }}>No next move recorded</span>}
                          </div>

                          <div className="wq-meta">
                            <DueBadge
                              date={waiting ? row.FollowUpIfNoResponseBy : row.NextFollowUpDate}
                              waiting={waiting}
                              label={waiting ? `Chase if silent by ${formatShortDate(row.FollowUpIfNoResponseBy)}` : undefined}
                            />
                            <span>
                              <span className="material-symbols-outlined">history</span>
                              Last contact {formatShortDate(row.LastInteractionAt)}
                            </span>
                            {row.OwnerUserId && (
                              <span>
                                <span className="material-symbols-outlined">person</span>
                                {row.OwnerUserId}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="wq-actions">
                          {waiting ? (
                            <>
                              <button className="btn secondary sm" type="button" onClick={() => void stopWaiting(row)}>
                                <span className="material-symbols-outlined">check</span>They replied
                              </button>
                              <Link className="btn ghost sm" to={`/relationships/people/${row.ContactId}`}>
                                Open
                              </Link>
                            </>
                          ) : followUp ? (
                            <>
                              <button
                                className="btn primary sm"
                                type="button"
                                onClick={() => setCompleting({ row, followUp })}
                              >
                                <span className="material-symbols-outlined">check</span>Complete
                              </button>
                              <button className="btn secondary sm" type="button" onClick={() => void defer(followUp, 7)}>
                                Defer 1w
                              </button>
                            </>
                          ) : (
                            <Link className="btn secondary sm" to={`/relationships/people/${row.ContactId}`}>
                              <span className="material-symbols-outlined">edit</span>Set next move
                            </Link>
                          )}
                        </div>
                      </article>
                    )
                  })}
                </div>
              )
            })}
          </>
        )}
      </div>

      {completing && (
        <CompleteFollowUpModal
          row={completing.row}
          followUp={completing.followUp}
          onClose={() => setCompleting(null)}
          onDone={() => void load()}
        />
      )}
    </>
  )
}

/**
 * Complete a follow-up.
 *
 * ONE call does six writes server-side: close it, log the activity, link the two, move
 * LastInteractionAt, set the next move and create the next follow-up. Recording an outcome and a
 * next move is what stops the loop going quiet, so both are in this one dialog rather than behind
 * a second visit nobody makes.
 */
export function CompleteFollowUpModal({
  row,
  followUp,
  onClose,
  onDone,
}: {
  row: RelationshipRow
  followUp: FollowUp
  onClose: () => void
  onDone: () => void
}) {
  const { options, notify, notifyError } = useApp()
  const [activityType, setActivityType] = useState(followUp.FollowUpType === 'Email' ? 'Email' : 'Phone Call')
  const [note, setNote] = useState('')
  const [outcome, setOutcome] = useState('')
  const [waiting, setWaiting] = useState(false)
  const [chaseDate, setChaseDate] = useState(addDaysIso(7))
  const [nextMove, setNextMove] = useState('')
  const [nextDate, setNextDate] = useState(addDaysIso(7))
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      await tfApi.completeFollowUp({
        FollowUpId: followUp.FollowUpId,
        CompletionNote: note || undefined,
        ActivityType: activityType,
        Direction: 'OUTBOUND',
        Outcome: outcome || undefined,
        CreatesWaitingForResponse: waiting,
        FollowUpIfNoResponseBy: waiting ? chaseDate : null,
        NextMove: waiting ? null : nextMove || null,
        NextFollowUpDate: waiting ? null : nextMove ? nextDate : null,
      })
      notify({
        tone: 'ok',
        title: 'Follow-up completed',
        body: waiting
          ? `${row.DisplayName} moved to Waiting — you will be reminded on ${formatShortDate(chaseDate)}`
          : nextMove
            ? `Activity logged and next move set for ${formatShortDate(nextDate)}`
            : 'Activity logged',
      })
      onDone()
      onClose()
    } catch (err) {
      notifyError('Could not complete the follow-up', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell
      title={`Complete — ${row.DisplayName}`}
      description={followUp.Description ?? 'Record what happened and what comes next.'}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Complete'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label className="form-label" htmlFor="cf-type">What did you do?</label>
          <select id="cf-type" className="form-input" value={activityType} onChange={(e) => setActivityType(e.target.value)}>
            {options('ACTIVITY_TYPE').map((v) => (
              <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="cf-when">Follow-up was due</label>
          <input id="cf-when" className="form-input" value={formatShortDate(followUp.FollowUpDate)} readOnly />
        </div>
        <div className="field span-2">
          <label className="form-label" htmlFor="cf-note">What happened?</label>
          <textarea id="cf-note" className="form-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div className="field span-2">
          <label className="form-label" htmlFor="cf-outcome">
            Outcome — worth far more in three weeks than “called”
          </label>
          <input
            id="cf-outcome"
            className="form-input"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            placeholder="Wants to reconnect after September 15"
          />
        </div>
      </div>

      <div className="banner info" style={{ marginTop: 14 }}>
        <span className="material-symbols-outlined">hourglass_top</span>
        <div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={waiting} onChange={(e) => setWaiting(e.target.checked)} />
            <b>They now owe me a reply</b>
          </label>
          <div style={{ marginTop: 4 }}>
            Moves this relationship to Waiting so it leaves your action list. This is what stops the
            third unnecessary email.
          </div>
        </div>
      </div>

      {waiting ? (
        <div className="field" style={{ marginTop: 12, maxWidth: 260 }}>
          <label className="form-label" htmlFor="cf-chase">Chase if no response by</label>
          <input id="cf-chase" className="form-input" type="date" value={chaseDate} onChange={(e) => setChaseDate(e.target.value)} />
        </div>
      ) : (
        <div className="form-grid" style={{ marginTop: 12 }}>
          <div className="field span-2">
            <label className="form-label" htmlFor="cf-next">Next move</label>
            <input
              id="cf-next"
              className="form-input"
              value={nextMove}
              onChange={(e) => setNextMove(e.target.value)}
              placeholder="Follow up after his September meeting"
            />
          </div>
          <div className="field">
            <label className="form-label" htmlFor="cf-nextdate">Next follow-up</label>
            <input id="cf-nextdate" className="form-input" type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
          </div>
        </div>
      )}
    </ModalShell>
  )
}
